import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AngleDownIconAtom } from '@atoms/icon-atoms/icon-atoms';
import { useSortBy } from 'react-instantsearch';
import { useCustomSearch } from '@components/search/CustomSearchContext';

type CustomSortItems = {
  value: string;
  label: string;
};

interface StaticTranslations {
  [key: string]: string;
}
interface CustomSortByProps {
  items: CustomSortItems[];
  staticTranslations: StaticTranslations[];
}

const CustomSortBy: React.FC<CustomSortByProps> = ({
  items,
  staticTranslations,
}) => {
  const { searchValue } = useCustomSearch();
  const [isMostRelevantDisabled, setIsMostRelevantDisabled] = useState(true);
  const localesMostRelevant = staticTranslations['Most relevant'];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');

    if (searchParam !== '' && searchParam !== null) {
      setIsMostRelevantDisabled(false);
    } else {
      setIsMostRelevantDisabled(true);
    }
  }, []);

  const { currentRefinement, refine } = useSortBy({ items });
  const [isOpen, setIsOpen] = useState(false);
  const selectWrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchValue !== '' && searchValue !== null) {
      // Refine to Most relevant when user input changes and is not empty string or null
      // Enable Most relevant option
      setIsMostRelevantDisabled(false);
      refine(items[4].value);
      return;
    }

    if (searchValue === '') {
      // Refine to Most Recent when the search query is empty
      // Disable Most relevant option
      setIsMostRelevantDisabled(true);
      refine(items[2].value);
      return;
    }
  }, [searchValue, refine, items]);

  const handleOutsideClick = e => {
    if (selectWrapper?.current?.contains(e.target)) {
      return;
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isOpen]);

  const selectedSorting = useMemo(() => {
    if (items) {
      const results = items.filter(item => item.value === currentRefinement);
      return results.length > 0 ? results[0] : { value: '', label: '' };
    }
    return { value: '', label: '' };
  }, [currentRefinement, items]);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="ais-RefinementList" ref={selectWrapper}>
      <ul
        className="ais-RefinementList-list"
        role="menu"
        aria-label="Sort by menu"
      >
        <li
          key="filterby"
          className="ais-RefinementList-item-showMore"
          role="none"
        >
          <button
            className="ais-RefinementList-showMore"
            data-testid="filter-dropdown"
            onClick={handleClick}
            role="menuitem"
          >
            {selectedSorting.label}
            <AngleDownIconAtom width="1.5rem" />
          </button>
        </li>
        {isOpen
          ? items.map(item => (
              <li key={item.value} className="ais-sortby-item" role="menuitem">
                <button
                  onClick={event => {
                    event.preventDefault();
                    refine(item.value);
                  }}
                  disabled={
                    localesMostRelevant === item.label && isMostRelevantDisabled
                  }
                >
                  {item.label}
                </button>
              </li>
            ))
          : !isOpen}
      </ul>
    </div>
  );
};

export default CustomSortBy;
